import { Box, CircularProgress } from '@mui/material';
import { Suspense, lazy } from 'react';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';
import AppShellProvider from '../components/shell/AppShellContext';
import { useGlobalState } from '../context/GlobalState';
import HomeContext from '../context/HomeContext';

// Lazy load all components
const ErrorPage = lazy(() => import('../components/ErrorPage'));
const HomeScreen = lazy(() => import('../components/home/HomeScreen'));
const RestaurantMapScreen = lazy(() => import('../components/restaurants/RestaurantMapScreen'));
const ScannerScreen = lazy(() => import('../components/scanner/ScannerScreen'));
const ItemsValidationScreen = lazy(() => import('../components/transform/ItemsValidationScreen'));
const LoginScreen = lazy(() => import('../components/login/LoginScreen'));
const AdminPanelScreen = lazy(() => import('../components/adminPanel/AdminPanelScreen'));
const Organisations = lazy(() => import('../components/adminPanel/Organisations'));
const CreateOrganisation = lazy(() => import('../components/adminPanel/CreateOrganisation'));
const Users = lazy(() => import('../components/adminPanel/Users'));
const CreateUser = lazy(() => import('../components/adminPanel/CreateUser'));
const OrganisationTabs = lazy(() => import('../components/adminPanel/editOrganisation/OrganisationTabs'));
const QrCodeGenerator = lazy(() => import('../components/adminPanel/QrCodeGenerator'));
const CreateArticleTemplate = lazy(() => import('../components/adminPanel/CreateArticleTemplate'));
const EditArticleTemplate = lazy(() => import('../components/adminPanel/editOrganisation/EditArticleTemplate'));
const EditUser = lazy(() => import('../components/adminPanel/editOrganisation/EditUser'));
const AlternateIdScreen = lazy(() => import('../components/transform/AlternateIdScreen'));
const SettingsTabs = lazy(() => import('../components/adminPanel/SettingsTabs'));
const SystemGroups = lazy(() => import('../components/adminPanel/SystemGroups'));
const SystemProviders = lazy(() => import('../components/adminPanel/SystemProviders'));
const ImportTool = lazy(() => import('../components/adminPanel/ImportTool'));
const GroupFortnox = lazy(() => import('../components/adminPanel/GroupFortnox'));
const FortnoxConnections = lazy(() => import('../components/adminPanel/fortnox/FortnoxConnections'));
const DevToolTabs = lazy(() => import('../components/adminPanel/devTool/DevToolTabs'));
const DuplicateCandidateDetail = lazy(() => import('../components/adminPanel/devTool/DuplicateCandidateDetail'));
const FortnoxComplete = lazy(() => import('../components/fortnox/FortnoxComplete'));
const GroupMapScreen = lazy(() => import('../components/map/GroupMapScreen'));
const LinkMapScreen = lazy(() => import('../components/map/LinkMapScreen'));
const ManagementPanelScreen = lazy(() => import('../components/managementPanel/ManagementPanelScreen'));
const ManagementOrganisations = lazy(() => import('../components/managementPanel/ManagementOrganisations'));
const ManagementOrganisationTabs = lazy(() => import('../components/managementPanel/ManagementOrganisationTabs'));
const OnboardingScreen = lazy(() => import('../components/onboarding/OnboardingScreen'));
const OnboardingApplications = lazy(() => import('../components/adminPanel/onboarding/OnboardingApplications'));
const AdminServices = lazy(() => import('../components/adminPanel/services/AdminServices'));
const OnboardingApplicationDetail = lazy(
  () => import('../components/adminPanel/onboarding/OnboardingApplicationDetail')
);

// Loading component
const LoadingSpinner = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100%'
    }}
  >
    <CircularProgress size={'3rem'} />
  </Box>
);

const RequiresSysAdmin = (props: { children: any }) => {
  const globalState = useGlobalState();

  // Check if we have tokens in localStorage to determine if we should wait for auth
  const hasTokens = localStorage.getItem('tractech_token') || localStorage.getItem('tractech_refresh_token');

  // If we have tokens but no user yet, show loading (auth is in progress)
  if (hasTokens && !globalState.user) {
    return <LoadingSpinner />;
  }

  // If we have a user and they're a super user, show the content
  if (globalState.user && globalState.user.isSuperUser) {
    return props.children;
  }

  // Otherwise redirect to home
  return <Navigate to={'/'} replace></Navigate>;
};

const RequiresAuth = (props: { children: any }) => {
  const globalState = useGlobalState();

  // Check if we have tokens in localStorage to determine if we should wait for auth
  const hasTokens = localStorage.getItem('tractech_token') || localStorage.getItem('tractech_refresh_token');

  // If we have tokens but no user yet, show loading (auth is in progress)
  if (hasTokens && !globalState.user) {
    return <LoadingSpinner />;
  }

  // If we have a user, show the content
  if (globalState.user) {
    return props.children;
  }

  // Otherwise redirect to home
  return <Navigate to={'/'} replace></Navigate>;
};
const RequiresUnAuth = (props: { children: any }) => {
  const globalState = useGlobalState();
  if (globalState.user) {
    return <Navigate to={globalState.user.isSuperUser ? '/admin/organisations' : '/management'} replace></Navigate>;
  }
  return props.children;
};

/** Gates the organisation self-service panel: any non-superuser is let in (even with zero mapped
 * organisations — the landing screen shows a "no organisation assigned" state rather than the
 * router bouncing them away); a superuser is sent to their own panel instead. */
const RequiresOrgAdmin = (props: { children: any }) => {
  const globalState = useGlobalState();

  const hasTokens = localStorage.getItem('tractech_token') || localStorage.getItem('tractech_refresh_token');
  if (hasTokens && !globalState.user) {
    return <LoadingSpinner />;
  }

  if (!globalState.user) {
    return <Navigate to={'/'} replace></Navigate>;
  }

  if (globalState.user.isSuperUser) {
    return <Navigate to={'/admin'} replace></Navigate>;
  }

  return props.children;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShellProvider />,
    errorElement: (
      <Suspense fallback={<LoadingSpinner />}>
        <ErrorPage />
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: (
          <HomeContext>
            <Suspense fallback={<LoadingSpinner />}>
              <HomeScreen />
            </Suspense>
          </HomeContext>
        )
      },
      {
        path: 'scanner',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ScannerScreen />
          </Suspense>
        )
      },
      {
        path: 'scanner/transform',
        element: (
          <RequiresAuth>
            <Suspense fallback={<LoadingSpinner />}>
              <ItemsValidationScreen />
            </Suspense>
          </RequiresAuth>
        )
      },
      {
        path: 'scanner/transform/alternate',
        element: (
          <RequiresAuth>
            <Suspense fallback={<LoadingSpinner />}>
              <AlternateIdScreen />
            </Suspense>
          </RequiresAuth>
        )
      },
      {
        path: 'login',
        element: (
          <RequiresUnAuth>
            <Suspense fallback={<LoadingSpinner />}>
              <LoginScreen />
            </Suspense>
          </RequiresUnAuth>
        )
      },
      {
        path: '/map/*',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <RestaurantMapScreen />
          </Suspense>
        )
      },
      {
        path: '/group/*',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <GroupMapScreen />
          </Suspense>
        )
      },
      {
        path: '/l/:linkId',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LinkMapScreen />
          </Suspense>
        )
      },
      {
        // Public: authorised by the emailed ?token=, not by a login.
        path: 'onboarding',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <OnboardingScreen />
          </Suspense>
        )
      },
      {
        path: 'fortnox/complete',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <FortnoxComplete />
          </Suspense>
        )
      },
      {
        path: 'admin',
        element: (
          <RequiresSysAdmin>
            <Suspense fallback={<LoadingSpinner />}>
              <AdminPanelScreen />
            </Suspense>
          </RequiresSysAdmin>
        ),
        children: [
          { index: true, element: <Navigate to={'/admin/organisations'} replace /> },
          {
            path: 'organisations',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <Organisations />
              </Suspense>
            )
          },
          {
            path: 'organisations/create',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <CreateOrganisation />
              </Suspense>
            )
          },
          {
            path: 'organisations/:orgId',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <OrganisationTabs />
              </Suspense>
            )
          },
          {
            path: 'organisations/:orgId/article-templates/create',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <CreateArticleTemplate />
              </Suspense>
            )
          },
          {
            path: 'organisations/:orgId/article-templates/:templateId',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <EditArticleTemplate />
              </Suspense>
            )
          },
          {
            path: 'applications',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <OnboardingApplications />
              </Suspense>
            )
          },
          {
            path: 'applications/:applicationId',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <OnboardingApplicationDetail />
              </Suspense>
            )
          },
          {
            path: 'services',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <AdminServices />
              </Suspense>
            )
          },
          {
            path: 'memberships',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <SystemGroups />
              </Suspense>
            )
          },
          {
            path: 'memberships/groups/:groupId/fortnox',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <GroupFortnox />
              </Suspense>
            )
          },
          {
            path: 'fortnox-connections',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <FortnoxConnections />
              </Suspense>
            )
          },
          {
            path: 'providers',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <SystemProviders />
              </Suspense>
            )
          },
          {
            path: 'import-tool',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <ImportTool />
              </Suspense>
            )
          },
          {
            path: 'users',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <Users />
              </Suspense>
            )
          },
          {
            path: 'users/create',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <CreateUser />
              </Suspense>
            )
          },
          {
            path: 'users/:userId/edit',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <EditUser />
              </Suspense>
            )
          },
          {
            path: 'qr-generator',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <QrCodeGenerator />
              </Suspense>
            )
          },
          {
            path: 'system-settings',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <SettingsTabs />
              </Suspense>
            )
          },
          {
            path: 'devtool',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <DevToolTabs />
              </Suspense>
            )
          },
          {
            path: 'devtool/duplicates/:candidateKey',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <DuplicateCandidateDetail />
              </Suspense>
            )
          }
        ]
      },
      {
        path: 'management',
        element: (
          <RequiresOrgAdmin>
            <Suspense fallback={<LoadingSpinner />}>
              <ManagementPanelScreen />
            </Suspense>
          </RequiresOrgAdmin>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <ManagementOrganisations />
              </Suspense>
            )
          },
          {
            path: 'organisations/:orgId',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <ManagementOrganisationTabs />
              </Suspense>
            )
          }
        ]
      }
    ]
  }
]);

const AppRoutes = () => {
  return <RouterProvider router={router} />;
};
export default AppRoutes;
